/**
 * MFA Security Settings — mandatory-MFA model (June 2026).
 *
 * MFA can never be disabled. This page offers exactly three things:
 *   1. Status: enrollment state + recovery codes remaining.
 *   2. Regenerate recovery codes (requires a fresh OTP, shown once).
 *   3. Replace authenticator device (password + old-device OTP →
 *      new QR → new-device OTP; old sessions are revoked).
 */
import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShieldCheck, Key, Copy, DeviceMobile } from '@phosphor-icons/react';
import ActiveSessionsCard from '../components/security/ActiveSessionsCard';
import ChangePasswordCard from '../components/security/ChangePasswordCard';

const RecoveryCodesBlock = ({ codes, onDone }) => {
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      toast.success('Recovery codes copied');
    } catch { toast.error('Clipboard copy failed'); }
  };
  return (
    <div className="space-y-4" data-testid="mfa-new-recovery-codes">
      <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
        These new codes replace ALL previous ones and are shown once. Save them now.
      </div>
      <div className="grid grid-cols-2 gap-2">
        {codes.map((c) => (
          <code key={c} className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-sm font-mono text-center">
            {c}
          </code>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={copyAll} data-testid="mfa-copy-new-codes-btn">
          <Copy size={14} className="mr-1" /> Copy all
        </Button>
        <Button onClick={onDone} data-testid="mfa-new-codes-done-btn">I&apos;ve saved them</Button>
      </div>
    </div>
  );
};

const RegenerateCodesCard = ({ remaining, onRegenerated }) => {
  const [otp, setOtp] = useState('');
  const [codes, setCodes] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/auth/mfa/recovery/regenerate', { otp: otp.trim() });
      setCodes(r.data.recovery_codes || []);
      setOtp('');
      onRegenerated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code did not verify');
    } finally { setLoading(false); }
  };

  return (
    <Card data-testid="mfa-regenerate-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Key size={18} className="text-indigo-700" /> Recovery codes
        </CardTitle>
        <CardDescription>
          {remaining} unused code{remaining === 1 ? '' : 's'} remaining. Regenerating
          invalidates every previous code and requires a fresh authenticator code.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {codes ? (
          <RecoveryCodesBlock codes={codes} onDone={() => setCodes(null)} />
        ) : (
          <form onSubmit={submit} className="flex items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="regen-otp" className="text-slate-700 text-xs">Authenticator code</Label>
              <Input
                id="regen-otp" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                placeholder="123456" value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                className="font-mono tracking-widest text-center max-w-[140px]"
                required data-testid="mfa-regen-otp-input"
              />
            </div>
            <Button type="submit" variant="outline" disabled={loading || otp.length !== 6} data-testid="mfa-regen-submit-btn">
              Regenerate codes
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

const ReplaceAuthenticatorCard = ({ onReplaced }) => {
  const [phase, setPhase] = useState('idle'); // idle | start | verify_new | done
  const [password, setPassword] = useState('');
  const [currentOtp, setCurrentOtp] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [newOtp, setNewOtp] = useState('');
  const [pending, setPending] = useState(null); // { secret, provisioning_uri }
  const [codes, setCodes] = useState(null);
  const [loading, setLoading] = useState(false);

  const startReplace = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/auth/mfa/replace-authenticator', {
        password,
        current_otp: useBackupCode ? undefined : currentOtp.trim(),
        backup_code: useBackupCode ? backupCode.trim() : undefined,
      });
      setPending(r.data);
      setCurrentOtp('');
      setBackupCode('');
      setPhase('verify_new');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally { setLoading(false); }
  };

  const finishReplace = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Old-device proof was recorded in phase 1 (10-minute window).
      const r = await api.post('/auth/mfa/replace-authenticator', {
        password, new_otp: newOtp.trim(),
      });
      setCodes(r.data.recovery_codes || []);
      setPhase('done');
      toast.success('Authenticator replaced. Save your new recovery codes.');
      onReplaced();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'New code did not verify');
    } finally { setLoading(false); }
  };

  const reset = () => {
    setPhase('idle'); setPassword(''); setCurrentOtp(''); setBackupCode('');
    setUseBackupCode(false); setNewOtp(''); setPending(null); setCodes(null);
  };

  return (
    <Card data-testid="mfa-replace-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <DeviceMobile size={18} className="text-indigo-700" /> Replace authenticator device
        </CardTitle>
        <CardDescription>
          Move MFA to a new phone without ever leaving your account unprotected. Your old
          device stays valid until the new one is verified; all other sessions are then signed out.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {phase === 'idle' && (
          <Button variant="outline" onClick={() => setPhase('start')} data-testid="mfa-replace-start-btn">
            Replace device
          </Button>
        )}
        {phase === 'start' && (
          <form onSubmit={startReplace} className="space-y-3 max-w-sm">
            <div className="space-y-1">
              <Label htmlFor="rep-pw" className="text-xs">Account password</Label>
              <Input id="rep-pw" type="password" value={password} required
                     onChange={(e) => setPassword(e.target.value)} data-testid="mfa-replace-password-input" />
            </div>
            {useBackupCode ? (
              <div className="space-y-1">
                <Label htmlFor="rep-backup" className="text-xs">One of your recovery codes</Label>
                <Input id="rep-backup" placeholder="XXXX-XXXX-XX" value={backupCode}
                       onChange={(e) => setBackupCode(e.target.value)}
                       className="font-mono tracking-widest text-center max-w-[200px]" required
                       data-testid="mfa-replace-backup-code-input" />
              </div>
            ) : (
              <div className="space-y-1">
                <Label htmlFor="rep-otp" className="text-xs">Code from your CURRENT device</Label>
                <Input id="rep-otp" inputMode="numeric" maxLength={6} placeholder="123456"
                       value={currentOtp} onChange={(e) => setCurrentOtp(e.target.value.replace(/[^0-9]/g, ''))}
                       className="font-mono tracking-widest text-center max-w-[140px]" required
                       data-testid="mfa-replace-current-otp-input" />
              </div>
            )}
            <button type="button"
                    className="block text-xs text-indigo-700 hover:underline"
                    onClick={() => setUseBackupCode(!useBackupCode)}
                    data-testid="mfa-replace-toggle-backup-btn">
              {useBackupCode ? 'I have my current device — use a code from it' : 'Lost this device? Use a recovery code instead'}
            </button>
            <div className="flex gap-3">
              <Button type="submit"
                      disabled={loading || (useBackupCode ? backupCode.trim().length < 10 : currentOtp.length !== 6)}
                      data-testid="mfa-replace-continue-btn">
                Continue
              </Button>
              <Button type="button" variant="outline" onClick={reset}>Cancel</Button>
            </div>
          </form>
        )}
        {phase === 'verify_new' && pending && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
              <div className="p-3 bg-white border border-slate-200 rounded-lg">
                <QRCode value={pending.provisioning_uri} size={150} data-testid="mfa-replace-qr" />
              </div>
              <div className="flex-1 text-sm space-y-2">
                <p className="text-slate-600">Scan with your NEW device, then enter the code it shows.</p>
                <code className="block bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-xs font-mono break-all">
                  {pending.secret}
                </code>
              </div>
            </div>
            <form onSubmit={finishReplace} className="space-y-3 max-w-sm">
              <div className="space-y-1">
                <Label className="text-xs">Code from your NEW device</Label>
                <Input inputMode="numeric" maxLength={6} placeholder="123456" value={newOtp}
                       onChange={(e) => setNewOtp(e.target.value.replace(/[^0-9]/g, ''))}
                       className="font-mono tracking-widest text-center max-w-[140px]" required
                       data-testid="mfa-replace-new-otp-input" />
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={loading || newOtp.length !== 6}
                        data-testid="mfa-replace-finish-btn">
                  Verify and swap
                </Button>
                <Button type="button" variant="outline" onClick={reset}>Cancel</Button>
              </div>
            </form>
          </div>
        )}
        {phase === 'done' && codes && (
          <RecoveryCodesBlock codes={codes} onDone={reset} />
        )}
      </CardContent>
    </Card>
  );
};

export default function MfaSettingsPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get('/auth/mfa/status');
      setStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load MFA status');
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="mfa-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Account Security
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          MFA is mandatory for every AZAI Billing account and cannot be disabled.
          Lost your device? An administrator can reset your MFA, which forces fresh
          enrollment at your next sign-in.
        </p>
      </div>

      <ActiveSessionsCard />

      <ChangePasswordCard />

      {status && (
        <Card data-testid="mfa-status-card">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck size={20} className="text-emerald-600" weight="duotone" />
                  MFA is active on {user?.email}
                </CardTitle>
                <CardDescription className="mt-1">
                  Enrollment status: {status.enrollment_status} ·{' '}
                  {status.recovery_codes_remaining} recovery code{status.recovery_codes_remaining === 1 ? '' : 's'} remaining
                </CardDescription>
              </div>
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="mfa-status-badge">
                Required &amp; Active
              </Badge>
            </div>
          </CardHeader>
          {status.recovery_codes_remaining < 3 && (
            <CardContent>
              <p className="text-xs text-amber-700" data-testid="mfa-low-codes-warning">
                ⚠ You are running low on recovery codes — regenerate a fresh set below.
              </p>
            </CardContent>
          )}
        </Card>
      )}

      {status?.mfa_enabled && (
        <>
          <RegenerateCodesCard remaining={status.recovery_codes_remaining} onRegenerated={loadStatus} />
          <ReplaceAuthenticatorCard onReplaced={loadStatus} />
        </>
      )}
    </div>
  );
}
